import { aiOpenapiClient } from '@/api/openapi-client';
import { env } from '@/env';
import { useCanUseAi } from '@/features/ai-assistant/hooks/useCanUseAi';
import {
  useAiChangeset,
  type AiChangesetAdapter,
} from '@/features/ai-changeset/hooks/useAiChangeset';
import { changesetApi } from '@/features/ai-changeset/utils/changeset-api';
import { lastDraft } from '@/features/ai-changeset/utils/last-draft';
import { buildMenuDraft, changeLabels, type MenuDraft } from '@/features/menu/utils/menu-draft';
import { summarizeMenuDraft } from '@/features/menu/utils/menu-draft-summary';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { AiChangesetView, MenuChange, MenuTree } from '@restaurant/api-contracts';
import {
  AiChangesetAppliedSchema,
  MenuChangesetStaleSchema,
  MenuChangesSchema,
  MenuQuickEditSessionSchema,
} from '@restaurant/api-contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

const EMPTY_DRAFT: MenuDraft = { sections: [], targets: [] };

export function useMenuQuickEdit(
  menuId: string,
  tree: MenuTree | undefined,
  view: () => Pick<AiChangesetView, 'sectionId' | 'search'>,
) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const currency = useRestaurantStore((s) => s.restaurantConfig?.currency) ?? 'EUR';
  const canUseAi = useCanUseAi();
  const copy = useT().menuQuickEdit;
  const qc = useQueryClient();
  const [draftOf] = useState(() => lastDraft(buildMenuDraft));

  const adapter = useMemo<AiChangesetAdapter<MenuChange>>(
    () => ({
      targets: (changes) => (tree ? draftOf(tree, changes).targets : []),
      labels: (changes) => (tree ? changeLabels(tree, changes) : []),
      summary: (changes) => (tree ? summarizeMenuDraft(tree, changes, copy.summary, currency) : ''),
    }),
    [tree, draftOf, copy.summary, currency],
  );

  const quickEdit = useAiChangeset<MenuChange>({
    changesSchema: MenuChangesSchema,
    id: `menu:${restaurantId}:${menuId}`,
    enabled: canUseAi && !!restaurantId,
    turnUrl: `${env.aiUrl}/restaurants/${restaurantId}/ai/menus/${menuId}/quick-edit`,
    pageName: 'menu-detail',
    fullViewLabel: copy.fullMenu,
    viewRows: 5,
    view,
    api: changesetApi<MenuChange, unknown>(
      {
        sessionSchema: MenuQuickEditSessionSchema,
        staleSchema: MenuChangesetStaleSchema,
        appliedSchema: AiChangesetAppliedSchema,
      },
      {
        load: (before) =>
          aiOpenapiClient.GET('/restaurants/{restaurantId}/ai/menus/{menuId}/quick-edit', {
            params: { path: { restaurantId, menuId }, query: { before } },
          }),
        apply: (turnId) =>
          aiOpenapiClient.POST(
            '/restaurants/{restaurantId}/ai/menus/{menuId}/quick-edit/turns/{turnId}/apply',
            { params: { path: { restaurantId, menuId, turnId } } },
          ),
        undo: (turnId) =>
          aiOpenapiClient.POST(
            '/restaurants/{restaurantId}/ai/menus/{menuId}/quick-edit/turns/{turnId}/undo',
            { params: { path: { restaurantId, menuId, turnId } } },
          ),
        discard: (turnId) =>
          aiOpenapiClient.POST(
            '/restaurants/{restaurantId}/ai/menus/{menuId}/quick-edit/turns/{turnId}/discard',
            { params: { path: { restaurantId, menuId, turnId } } },
          ),
      },
    ),
    onApplySettled: () => qc.invalidateQueries({ queryKey: ['menu'] }),
    adapter,
  });

  const draft = tree ? draftOf(tree, quickEdit.changes) : EMPTY_DRAFT;

  return { ...quickEdit, draft };
}

export type MenuQuickEdit = ReturnType<typeof useMenuQuickEdit>;
